import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ModalConfirmation from "../components/ModalConfirmation";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase-config";
import { UserContext } from "../contexts/userContext";
import { getFormattedDate } from "../utils/utils";
import { IPaymentMethod } from "../interfaces";
import { BUSINESS_COLLECTION, PAYMENTS_COLLECTION } from "../utils/constants";

const PaymentMethods = () => {
  const { businessData } = useContext(UserContext);
  const [payment, setPayment] = useState<string>("");
  const [paymentMethods, setPaymentMethods] = useState([] as IPaymentMethod[]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState({} as IPaymentMethod);
  const [openModal, setOpenModal] = useState(
    {} as { show: boolean; type: "delete" | "disable" | "" }
  );

  // const getPaymentMethods = async () => {
  //   setLoading(true);

  //   const { docs } = await getDocs(
  //     query(subCollectionRef, orderBy("createdAt"))
  //   ).finally(() => setLoading(false));

  //   if (docs.length) {
  //     const paymentsList = docs.map((doc) => ({
  //       ...doc.data(),
  //       id: doc.id,
  //       createdAt: getFormattedDate(doc.data().createdAt),
  //       updatedAt: getFormattedDate(doc.data().updatedAt),
  //     })) as IPaymentMethod[];
  //     setPaymentMethods(paymentsList);
  //     localStorage.setItem("paymentMethods", JSON.stringify(paymentsList));
  //   } else setPaymentMethods([]);
  // };

  const handleClose = () => {
    setOpenModal({ show: false, type: "" });
    setSelectedPayment({} as IPaymentMethod);
  };

  const updateItem = async (valueToUpdate: any) => {
    const subCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      PAYMENTS_COLLECTION
    );
    const productRef = doc(subCollectionRef, selectedPayment.id);
    await updateDoc(productRef, valueToUpdate as any).then(() => {
      setPayment("");
      setIsEditing(false);
    });
  };

  const handleEdit = async () => {
    setLoading(true);
    const itemToUpdate = {
      name: payment,
      updatedAt: new Date(),
    };

    updateItem(itemToUpdate);
  };

  const handleDelete = async () => {
    setLoading(true);
    const paymentMethodRef = doc(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      PAYMENTS_COLLECTION,
      selectedPayment.id
    );
    await deleteDoc(paymentMethodRef).finally(() => {
      setLoading(false);
      handleClose();
    });
  };

  const handleSwitchStatus = () => {
    const itemToUpdate = {
      status: selectedPayment.status === "enabled" ? "disabled" : "enabled",
    };
    updateItem(itemToUpdate);
    handleClose();
  };

  const handleDeleteOrSwitch = () => {
    switch (openModal.type) {
      case "delete":
        return handleDelete();
      case "disable":
        return handleSwitchStatus();
      default:
        return null;
    }
  };

  const handleSubmit = async () => {
    if (isEditing) return handleEdit();
    const subCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      PAYMENTS_COLLECTION
    );
    setLoading(true);
    const item = {
      name: payment,
      status: "enabled",
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await addDoc(subCollectionRef, item).then(() => {
      setPayment("");
    });
  };

  const handleShowConfirmation = (
    payment: IPaymentMethod,
    type: "delete" | "disable" | ""
  ) => {
    setSelectedPayment(payment);
    setOpenModal({ show: true, type });
  };

  useEffect(() => {
    setLoading(true);
    if (businessData.uid) {
      const subCollectionRef = collection(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        PAYMENTS_COLLECTION
      );
      const q = query(subCollectionRef, orderBy("createdAt", "desc"));
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          if (snapshot.docs.length) {
            const list = snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
              createdAt: getFormattedDate(doc.data().createdAt),
              updatedAt: getFormattedDate(doc.data().updatedAt),
            })) as IPaymentMethod[];
            setPaymentMethods(list);
            localStorage.setItem("paymentMethods", JSON.stringify(list));
          } else setPaymentMethods([]);
          setLoading(false);
        },
        (error) => {
          console.error("Error al obtener categorias en tiempo real: ", error);
          setLoading(false); // También desactivar el loading si hay un error
        }
      );

      return () => unsubscribe();
    }
  }, [businessData]);

  return (
    <Grid
      container
      display="flex"
      spacing={3}
      p={3}
      mt={10}
      sx={{ width: "100%" }}
    >
      <Grid size={{ xs: 12 }}>
        <Typography fontSize={24}>Metodos de pago</Typography>
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <TextField
          label="Nombre"
          size="small"
          color="secondary"
          fullWidth
          disabled={loading}
          value={payment}
          onChange={({ target: { value } }) => setPayment(value)}
        />
      </Grid>
      <Grid size={{ xs: 6, md: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          disabled={!payment || loading}
          onClick={handleSubmit}
        >
          {isEditing ? "Editar " : "Agregar "}medio de pago
        </Button>
      </Grid>
      <Grid size={{ xs: 12 }} display="flex" justifyContent="center">
        {loading ? (
          <CircularProgress size={120} sx={{ mt: 4 }} />
        ) : (
          <>
            {paymentMethods.length > 0 ? (
              <TableContainer component={Paper}>
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha de creación</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell align="center">Ultima actualización</TableCell>
                      <TableCell align="center">Estado</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentMethods.map((payment: IPaymentMethod) => (
                      <TableRow key={payment.id}>
                        <TableCell>{payment.createdAt}</TableCell>
                        <TableCell>{payment.name}</TableCell>
                        <TableCell align="center">
                          {payment.updatedAt}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            color={
                              payment.status === "enabled"
                                ? "success"
                                : "warning"
                            }
                            label={
                              payment.status === "enabled"
                                ? "Habilitada"
                                : "Deshabilitada"
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar">
                            <IconButton
                              onClick={() => {
                                setPayment(payment.name);
                                setSelectedPayment(payment);
                                setIsEditing(true);
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }}
                            >
                              <span className="material-symbols-rounded">
                                edit
                              </span>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              color="error"
                              onClick={() =>
                                handleShowConfirmation(payment, "delete")
                              }
                            >
                              <span className="material-symbols-rounded">
                                delete
                              </span>
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              payment.status === "enabled"
                                ? "Deshabilitar"
                                : "Habilitar"
                            }
                          >
                            <Switch
                              size="small"
                              color="secondary"
                              checked={payment.status === "enabled"}
                              onChange={() =>
                                handleShowConfirmation(payment, "disable")
                              }
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="warning">
                No hay metodos de pagos cargados.
              </Alert>
            )}
          </>
        )}
      </Grid>
      {openModal.show && (
        <ModalConfirmation
          page="payment"
          status={selectedPayment.status}
          openModal={openModal}
          handleClose={handleClose}
          handleSubmit={handleDeleteOrSwitch}
        />
      )}
    </Grid>
  );
};

export default PaymentMethods;
